import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControlLabel,
    Typography,
    Checkbox,
    FormGroup,
    Box,
    IconButton,
    Paper,
    Divider,
    ToggleButtonGroup,
    ToggleButton,
} from "@mui/material";
import { MyCheckCircleIcon, MyCloseIcon, MyDownloadIcon, MyRadioButtonUncheckedIcon, MyTableRowsIcon, MyViewWeekIcon } from "../../Assets/svg/MyIcons";

const ExportDialog = ({
    open,
    onClose,
    exportScope,
    setExportScope,
    onExport,
    columns = [],
    selectedColumns = [],
    setSelectedColumns,
}) => {
    const [localExportScope, setLocalExportScope] = useState(exportScope);
    const [localSelectedColumns, setLocalSelectedColumns] = useState(
        selectedColumns.length > 0
            ? selectedColumns
            : columns
                .filter((col) => col.field !== "actions")
                .map((col) => col.field)
    );

    const filteredColumns = columns.filter((col) => col.field !== "actions");

    const handleCheckboxChange = (field) => {
        setLocalSelectedColumns((prev) =>
            prev.includes(field)
                ? prev.filter((col) => col !== field)
                : [...prev, field]
        );
    };

    const handleSelectAll = () => {
        setLocalSelectedColumns(filteredColumns.map((col) => col.field));
    };

    const handleDeselectAll = () => {
        setLocalSelectedColumns([]);
    };

    const handleExport = () => {
        if (onExport) {
            onExport(localSelectedColumns, localExportScope);
        }
        onClose();
    };

    const handleScopeChange = (event, newScope) => {
        if (newScope !== null) {
            setLocalExportScope(newScope);
            setExportScope(newScope);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                    overflow: "hidden",
                },
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pb: 2,
                    pt: 3,
                    px: 3,
                    background:
                        "linear-gradient(135deg, #22419E 0%, #092b91ff 100%)",
                    color: "white",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <MyDownloadIcon sx={{ fontSize: 24 }} />
                    <Typography variant="h6" component="div" fontWeight={600}>
                        Export Data
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        color: "white",
                        "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.1)",
                        },
                    }}
                >
                    <MyCloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 3, px: 3, mt: 2 }}>
                {/* Export Scope Selection - Toggle Button Style */}
                <Box sx={{ mb: 3 }}>
                    <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        gutterBottom
                        sx={{ color: "#22419E", mb: 2 }}
                    >
                        Data Scope
                    </Typography>
                    <ToggleButtonGroup
                        value={localExportScope}
                        exclusive
                        onChange={handleScopeChange}
                        fullWidth
                        sx={{
                            "& .MuiToggleButton-root": {
                                border: "2px solid #e0e0e0",
                                borderRadius: "12px !important",
                                textTransform: "none",
                                py: 1.5,
                                px: 2,
                                "&:first-of-type": {
                                    borderTopRightRadius: "12px !important",
                                    borderBottomRightRadius: "12px !important",
                                    marginRight: 1,
                                },
                                "&:last-of-type": {
                                    borderTopLeftRadius: "12px !important",
                                    borderBottomLeftRadius: "12px !important",
                                    marginLeft: 1,
                                },
                                "&.Mui-selected": {
                                    backgroundColor: "#3a5ab9ff",
                                    color: "white",
                                    borderColor: "#3a5ab9ff",
                                    "&:hover": {
                                        backgroundColor: "#22419E",
                                        borderColor: "#22419E",
                                    },
                                },
                                "&:hover": {
                                    backgroundColor: "#f5f5f5",
                                    borderColor: "#22419E",
                                },
                            },
                        }}
                    >
                        <ToggleButton value="thisPage" sx={{ flex: 1 }}>
                            <Box sx={{ textAlign: "left", width: "100%" }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 0.5,
                                    }}
                                >
                                    <MyViewWeekIcon sx={{ fontSize: 18 }} />
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                    >
                                        Current Page
                                    </Typography>
                                </Box>
                                <Typography variant="caption">
                                    Export visible data only
                                </Typography>
                            </Box>
                        </ToggleButton>
                        <ToggleButton value="allData" sx={{ flex: 1 }}>
                            <Box sx={{ textAlign: "left", width: "100%" }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 0.5,
                                    }}
                                >
                                    <MyTableRowsIcon sx={{ fontSize: 18 }} />
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                    >
                                        All Data
                                    </Typography>
                                </Box>
                                <Typography variant="caption">
                                    Export complete dataset
                                </Typography>
                            </Box>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Column Selection */}
                <Box>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 2,
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            sx={{ color: "#22419E" }}
                        >
                            Select ({localSelectedColumns.length}/
                            {filteredColumns.length})
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <IconButton
                                onClick={handleSelectAll}
                                disabled={
                                    localSelectedColumns.length ===
                                    filteredColumns.length
                                }
                                sx={{
                                    color: "#22419E",
                                    border: "1px solid #22419E",
                                    borderRadius: 2,
                                    px: 2,
                                    py: 0.5,
                                    fontSize: "0.75rem",
                                    "&:hover": {
                                        backgroundColor: "#22419E",
                                        color: "white",
                                    },
                                    "&:disabled": {
                                        borderColor: "#e0e0e0",
                                        color: "#bdbdbd",
                                    },
                                }}
                            >
                                <MyCheckCircleIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                <Typography variant="caption" fontWeight={600}>
                                    Select All
                                </Typography>
                            </IconButton>
                            <IconButton
                                onClick={handleDeselectAll}
                                disabled={localSelectedColumns.length === 0}
                                sx={{
                                    color: "#757575",
                                    border: "1px solid #757575",
                                    borderRadius: 2,
                                    px: 2,
                                    py: 0.5,
                                    fontSize: "0.75rem",
                                    "&:hover": {
                                        backgroundColor: "#757575",
                                        color: "white",
                                    },
                                    "&:disabled": {
                                        borderColor: "#e0e0e0",
                                        color: "#bdbdbd",
                                    },
                                }}
                            >
                                <MyRadioButtonUncheckedIcon
                                    sx={{ fontSize: 16, mr: 0.5 }}
                                />
                                <Typography variant="caption" fontWeight={600}>
                                    Deselect All
                                </Typography>
                            </IconButton>
                        </Box>
                    </Box>

                    <Paper
                        variant="outlined"
                        sx={{
                            maxHeight: 280,
                            overflow: "auto",
                            borderRadius: 2,
                            border: "2px solid #f5f5f5",
                        }}
                    >
                        <FormGroup>
                            {filteredColumns.map((col) => (
                                <FormControlLabel
                                    key={col.field}
                                    control={
                                        <Checkbox
                                            checked={localSelectedColumns.includes(
                                                col.field
                                            )}
                                            onChange={() =>
                                                handleCheckboxChange(col.field)
                                            }
                                            size="small"
                                            sx={{
                                                color: "#22419E",
                                                "&.Mui-checked": {
                                                    color: "#22419E",
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                width: "100%",
                                                py: 0.5,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                fontWeight={500}
                                            >
                                                {col.headerName || col.field}
                                            </Typography>
                                            {/* {localSelectedColumns.includes(col.field) && (
                        <Chip
                          label="✓"
                          size="small"
                          sx={{
                            height: 20,
                            width: 20,
                            minWidth: 20,
                            marginLeft: 1,
                            fontSize: "0.7rem",
                            backgroundColor: "#22419E",
                            color: "white",
                            "& .MuiChip-label": {
                              px: 0,
                            },
                          }}
                        />
                      )} */}
                                        </Box>
                                    }
                                    sx={{
                                        ml: 0,
                                        mr: 0,
                                        mx: 1,
                                        my: 0.25,
                                        borderRadius: 2,
                                        transition: "all 0.2s ease",
                                        "&:hover": {
                                            bgcolor: "#f8f9fa",
                                        },
                                        ...(localSelectedColumns.includes(
                                            col.field
                                        ) && {
                                            bgcolor: "#e8f5e8",
                                            "&:hover": {
                                                bgcolor: "#d4edda",
                                            },
                                        }),
                                    }}
                                />
                            ))}
                        </FormGroup>
                    </Paper>
                </Box>

                {/* Export Summary */}
                {localSelectedColumns.length > 0 && (
                    <Paper
                        elevation={0}
                        sx={{
                            mt: 3,
                            p: 2.5,
                            backgroundColor: "#e0f2f1",
                            border: "2px solid #22419E",
                            borderRadius: 2,
                        }}
                    >
                        <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{
                                color: "#22419E",
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                            }}
                        >
                            Export Summary
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                mt: 0.5,
                                color: "#22419E",
                                fontWeight: 500,
                            }}
                        >
                            {localSelectedColumns.length} columns •{" "}
                            {localExportScope === "thisPage"
                                ? "Current page data"
                                : "Complete dataset"}
                        </Typography>
                    </Paper>
                )}
            </DialogContent>

            {/* Actions */}
            <DialogActions
                sx={{
                    p: 3,
                    pt: 2,
                    gap: 2,
                    backgroundColor: "#fafafa",
                }}
            >
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        minWidth: 100,
                        borderColor: "#e0e0e0",
                        color: "#757575",
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        "&:hover": {
                            borderColor: "#bdbdbd",
                            backgroundColor: "#f5f5f5",
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleExport}
                    variant="contained"
                    startIcon={<MyDownloadIcon />}
                    disabled={localSelectedColumns.length === 0}
                    sx={{
                        minWidth: 140,
                        fontWeight: 600,
                        borderRadius: 2,
                        textTransform: "none",
                        backgroundColor: "#22419E",
                        boxShadow: "0 4px 12px rgba(0, 121, 107, 0.3)",
                        "&:hover": {
                            backgroundColor: "#0d37b3ff",
                            boxShadow: "0 6px 16px rgba(0, 121, 107, 0.4)",
                        },
                        "&:disabled": {
                            backgroundColor: "#e0e0e0",
                            boxShadow: "none",
                        },
                    }}
                >
                    Export Data
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExportDialog;
